import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
import React, { useEffect, useState, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, History, Contact, Plus } from 'lucide-react';
import { fetchPersonal, Personal } from '../../../services/personalService';
import EditPersonnelModal from '../../../components/Personnel/EditPersonnelModal';
import DeletePersonnelModal from '../../../components/Personnel/DeletePersonnelModal';
import AddPersonnelModal from '../../../components/Personnel/AddPersonnelModal';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const PersonnelPage: React.FC = () => {
    const [personal, setPersonal] = useState<Personal[]>([]);
    const [selectedPerson, setSelectedPerson] = useState<Personal | null>(null);
    const [personToDelete, setPersonToDelete] = useState<Personal | null>(null);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();

    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('personal', userLevelId);

    const loadPersonal = useCallback(async () => {
        if (!perm?.can_read) {
            setPersonal([]);
            return;
        }
        try {
            const datos = await fetchPersonal();
            setPersonal(datos.sort((a, b) => a.id - b.id));
        } catch (error) {
            /* Browser diagnostics omitted. */
            setPersonal([]);
        }
    }, [perm?.can_read]);

    useEffect(() => {
        loadPersonal();
    }, [loadPersonal]);

    const handleDeleteSuccess = () => {
        setPersonal(prev => prev.filter(p => p.id !== personToDelete?.id));
        setPersonToDelete(null);
    };

    const handleAddPersonnel = (nuevoPersonal: Personal) => {
        setPersonal(prev => {
            const updated = [...prev, nuevoPersonal];
            return updated.sort((a, b) => a.id - b.id);
        });
    };

    // Lógica de paginación
    const totalPages = Math.ceil(personal.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedPersonal = personal.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null) return <Loading text="Cargando personal..." />;

    return <><LumaResourceWorkspace title="Personal" description="Consulta y gestiona la información de tu coworking." group="Usuarios" rows={personal.map(r=>({...r,nombre_completo:`${r.usuario?.first_name||''} ${r.usuario?.last_name||''}`}))} columns={[{key:'usuario',label:'Nombre',render:r=>`${r.usuario?.first_name||''} ${r.usuario?.last_name||''}`},{key:'email',label:'Email',render:r=>r.usuario?.email||'—'},{key:'cargo',label:'Cargo'},{key:'telefono',label:'Teléfono'},{key:'estado',label:'Estado',render:r=>r.usuario?.estado===undefined?'—':r.usuario.estado?'Activo':'Inactivo'}]} idKey="id" nameKey="nombre_completo" statusKey="estado" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setIsAddOpen(true)}:undefined} onEdit={perm?.can_update?setSelectedPerson:undefined} onDelete={perm?.can_delete?setPersonToDelete:undefined} history=""/>{selectedPerson&&<EditPersonnelModal
                        isOpen={!!selectedPerson}
                        onClose={() => setSelectedPerson(null)}
                        person={selectedPerson}
                        onUpdate={(updated) => {
                            setPersonal(prev => prev.map(p => p.id === updated.id ? updated : p));
                        }}
                    />}{personToDelete&&<DeletePersonnelModal
                        isOpen={!!personToDelete}
                        onClose={() => setPersonToDelete(null)}
                        person={personToDelete}
                        onDeleteSuccess={handleDeleteSuccess}
                    />}<AddPersonnelModal
                    isOpen={isAddOpen}
                    onClose={() => setIsAddOpen(false)}
                    onAddPersonnel={handleAddPersonnel}
                /><ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                /></>;
};

export default PersonnelPage;
